import { QueryError } from '@graphql-box/helpers';

import { useQuery } from '@graphql-box/react';
import { type ReactElement, useEffect } from 'react';
import { ScreenFormatType } from '#enums/mediaTypes.ts';
import {
  type EpisodeCastAndCrewData,
  type MovieCastAndCrewData,
  type SeasonCastAndCrewData,
  type TvCastAndCrewData,
} from '../types.ts';
import { Component } from './Component.tsx';
import { formatCastAndCrewHighlights } from './helpers/formatCastAndCrewHighlights.ts';

const queries = {
  [ScreenFormatType.Episode]: "\nquery GetEpisodeCastAndCrew(\n  $id: ID!\n) {\n  episode(id: $id) {\n    ...EpisodeCastAndCrew\n  }\n}\n\nfragment EpisodeCastAndCrew on Episode {\n  cast {\n    character\n    name\n    profilePath\n  }\n  crew {\n    job\n    name\n    profilePath\n  }\n  guestStars {\n    character\n    name\n    profilePath\n  }\n}",
  [ScreenFormatType.Movie]: "\nquery GetMovieCastAndCrew(\n  $id: ID!\n) {\n  movie(id: $id) {\n    ...MovieCastAndCrew\n  }\n}\n\nfragment MovieCastAndCrew on Movie {\n  cast {\n    character\n    name\n    profilePath\n  }\n  crew {\n    job\n    name\n    profilePath\n  }\n}\n",
  [ScreenFormatType.Season]: "\nquery GetSeasonCastAndCrew(\n  $id: ID!\n) {\n  season(id: $id) {\n    ...SeasonCastAndCrew\n  }\n}\n\nfragment SeasonCastAndCrew on Season {\n  cast {\n    character\n    name\n    profilePath\n  }\n  crew {\n    job\n    name\n    profilePath\n  }\n}\n",
  [ScreenFormatType.Tv]: "\nquery GetTvCastAndCrew(\n  $id: ID!\n) {\n  tv(id: $id) {\n    ...TvCastAndCrew\n  }\n}\n\nfragment TvCastAndCrew on Tv {\n  cast {\n    character\n    name\n    profilePath\n  }\n  crew {\n    job\n    name\n    profilePath\n  }\n}\n",
};

export type QueryProps = {
  children?: (element: ReactElement) => ReactElement;
  id: string | number;
  type: ScreenFormatType;
};

export const Query = ({ children, id, type }: QueryProps) => {
  const query = queries[type];

  const { execute, loading, result } = useQuery<
    EpisodeCastAndCrewData | MovieCastAndCrewData | SeasonCastAndCrewData | TvCastAndCrewData
  >(query, {
    loading: true,
  });

  useEffect(() => {
    void execute({ variables: { id } }, { data: { initiator: 'Highlights' } });
    // We only want to re-execute when id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (result instanceof QueryError) {
    console.error(result);
    return;
  }

  if (result?.errors?.length) {
    console.error(result.errors);
  }

  const highlights = formatCastAndCrewHighlights(result?.data);

  if (!loading && highlights.length === 0) {
    return;
  }

  return children ? children(<Component highlights={highlights} />) : <Component highlights={highlights} />;
};
